import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, AlertTriangle, Clock, Banknote, CreditCard, FileText, Landmark, Mail, Download, Lock, Layers } from "lucide-react";
import { toast } from "sonner";
import { CompanyTotalsCard } from "@/components/CompanyTotalsCard";
import CashFlowCard from "@/components/CashFlowCard";
import ExpensesSection from "@/components/ExpensesSection";
import AmountInput from "@/components/AmountInput";
import MultiPolizzaDialog from "@/components/MultiPolizzaDialog";

const METHODS_BASIC = ["Contante", "Assegno", "Bancomat", "Bonifico", "Bollettino postale"];
// Sub-tipi sospeso granulari — l'agente sceglie quali attivare (agency.sospesi_metodi).
// Ogni Sospeso X abilitato porta con sé anche il Recupero X (se recuperi_abilitati=true).
const SOSPESO_SUBMETHODS = {
  contante: { sospeso: "Sospeso contante", recupero: "Recupero contante" },
  assegno:  { sospeso: "Sospeso assegno",  recupero: "Recupero assegno" },
  bonifico: { sospeso: "Sospeso bonifico", recupero: "Recupero bonifico" },
  pos:      { sospeso: "Sospeso POS",      recupero: "Recupero POS" },
  postale:  { sospeso: "Sospeso postale",  recupero: "Recupero postale" },
};
const METHODS_AGENCY = [];
const METHODS_WITHOUT_POLIZZA = [];
const METHOD_ICONS = {
  Contante: Banknote,
  Assegno: FileText,
  Bancomat: CreditCard,
  Bonifico: Landmark,
  "Bollettino postale": Mail,
  "Recupero contante": Banknote,
  "Recupero assegno": FileText,
  "Recupero bonifico": Landmark,
  "Recupero POS": CreditCard,
  "Recupero postale": Mail,
  "Incasso collaboratore": Banknote,
  Sospeso: Clock,
  "Sospeso contante": Clock,
  "Sospeso assegno": Clock,
  "Sospeso bonifico": Clock,
  "Sospeso POS": Clock,
  "Sospeso postale": Clock,
};

function formatEUR(v) {
  const n = Number(v || 0);
  return n.toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}

function formatDateIT(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function emptyDraft() {
  return {
    urgente: false,
    contraente: "",
    numero_polizza: "",
    importo: "",
    abbuono: "",
    metodo_pagamento: "Contante",
    compagnia_id: "",
    note: "",
    collab_associato_nome: "",
  };
}

// Auto-save del draft in localStorage: chiave scoped per sheet.id così due giorni
// consecutivi o due utenti sullo stesso browser non si sovrascrivono.
const DRAFT_STORAGE_PREFIX = "incassi:draft:sheet:";
function draftStorageKey(sheetId) {
  return sheetId ? `${DRAFT_STORAGE_PREFIX}${sheetId}` : null;
}
function isDraftEmpty(d) {
  if (!d) return true;
  const emptyStrings = !d.contraente && !d.numero_polizza && !d.importo && !d.abbuono && !d.compagnia_id && !d.note;
  const defaultMethod = d.metodo_pagamento === "Contante" || !d.metodo_pagamento;
  return emptyStrings && !d.urgente && defaultMethod;
}

export default function CollaboratorSheet() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [closingInfo, setClosingInfo] = useState({ today_closing: null });
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);
  const [multiOpen, setMultiOpen] = useState(false);

  const { id: sheetIdParam } = useParams();
  const load = useCallback(async ({ silent = false } = {}) => {
    // Solo il primo caricamento mostra lo skeleton. I refresh (event-based) sono
    // silenziosi per non smontare il form e perdere lo stato locale (draft,
    // MultiPolizzaDialog, ecc.). Vedi bug "reload al cambio finestra".
    if (!silent) setLoading(true);
    try {
      const sheetUrl = sheetIdParam ? `/cash-sheets/${sheetIdParam}` : "/cash-sheets/current";
      const [s, pub] = await Promise.all([
        api.get(sheetUrl),
        api.get("/settings/public"),
      ]);
      setData(s.data);
      setClosingInfo(pub.data || {});
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [sheetIdParam]);

  useEffect(() => { load(); }, [load]);

  // Expose load to window so cross-component events can trigger a silent refresh
  useEffect(() => {
    window.__cashSheetReload = () => load({ silent: true });
    return () => { delete window.__cashSheetReload; };
  }, [load]);

  // Listen for agency updates (e.g. plurimandatari toggled in /admin/abbonamento).
  // NON ascoltiamo più "focus": causava un reload aggressivo al ritorno sulla tab
  // che smontava il form (Caricamento foglio cassa…) facendo perdere draft e
  // MultiPolizzaDialog aperto. Le mutazioni intra-app emettono già "agency:updated".
  useEffect(() => {
    const handler = () => { window.__cashSheetReload && window.__cashSheetReload(); };
    window.addEventListener("agency:updated", handler);
    return () => {
      window.removeEventListener("agency:updated", handler);
    };
  }, []);

  const isOpen = data?.sheet?.stato === "aperto" || data?.sheet?.stato === "riaperto";
  const sheet = data?.sheet;
  const entries = data?.entries || [];
  const user = data?.user;
  const totals = data?.totals || {};

  // Ripristino del draft dal localStorage quando il foglio diventa noto (una volta sola per sheet.id).
  const [draftRestored, setDraftRestored] = useState(false);
  useEffect(() => {
    if (!sheet?.id || draftRestored) return;
    const key = draftStorageKey(sheet.id);
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && typeof saved === "object" && !isDraftEmpty(saved)) {
          setDraft({ ...emptyDraft(), ...saved });
          toast.info("Ripristinato incasso in bozza");
        }
      }
    } catch (err) { /* localStorage disabilitato o JSON corrotto: ignoriamo ma tracciamo in dev */
      if (typeof console !== "undefined") console.debug("draft restore skipped:", err?.message || err);
    }
    setDraftRestored(true);
  }, [sheet?.id, draftRestored]);

  // Persistenza del draft: salva ad ogni modifica, cancella se torna vuoto.
  useEffect(() => {
    if (!sheet?.id || !draftRestored) return;
    const key = draftStorageKey(sheet.id);
    try {
      if (isDraftEmpty(draft)) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, JSON.stringify(draft));
      }
    } catch (err) { /* quota exceeded / privacy mode: silenzioso */
      if (typeof console !== "undefined") console.debug("draft save skipped:", err?.message || err);
    }
  }, [draft, sheet?.id, draftRestored]);

  const isAgente = user?.role === "agente";
  const isAutonomous = isAgente
    ? closingInfo.agency_sheet_follows_closing_times === false
    : !!user?.chiusura_autonoma;
  const allowAbbuoni = isAgente || !!user?.abbuoni_abilitati;
  const sospesiEnabled = !!data?.agency?.sospesi_abilitati;
  const recuperiEnabled = !!data?.agency?.recuperi_abilitati;
  const plurimandatari = !!data?.agency?.plurimandatari_abilitato;
  const multiEnabled = !!data?.agency?.multi_polizza_enabled;
  // Colonna "Collab. associato" — solo sul foglio dell'agente titolare + flag agency ON.
  // Accessibile ad agente e utenza interna (che condividono lo stesso foglio agenzia).
  const showCollabCol = (user?.role === "agente" || user?.role === "interno")
    && !!data?.agency?.fc_agenzia_collab_col_enabled;
  const [collabOptions, setCollabOptions] = useState([]);
  useEffect(() => {
    if (!showCollabCol) return;
    let cancelled = false;
    api.get("/agency/collab-associato-options")
      .then((r) => { if (!cancelled) setCollabOptions(r.data?.options || []); })
      .catch(() => { if (!cancelled) setCollabOptions([]); });
    return () => { cancelled = true; };
  }, [showCollabCol]);
  const companies = data?.companies || [];
  const activeCompanies = companies.filter((c) => c.attivo);
  // Lock urgent entries: once confirmed (email sent), only the agent can modify/delete
  const isLockedForCollab = (e) => !!(e?.urgente && e?.urgent_email_sent && !isAgente);
  // Ordine di preferenza dei sub-metodi (visualizzazione dropdown)
  const sospesiMetodi = Array.isArray(data?.agency?.sospesi_metodi) ? data.agency.sospesi_metodi : [];
  const buildSospesoLabels = () => {
    if (sospesiMetodi.length === 0) return sospesiEnabled ? ["Sospeso"] : [];
    // Ordine fisso: contante, assegno, bonifico, pos, postale
    return ["contante", "assegno", "bonifico", "pos", "postale"]
      .filter((k) => sospesiMetodi.includes(k))
      .map((k) => SOSPESO_SUBMETHODS[k].sospeso);
  };
  const buildRecuperoLabels = () => {
    if (!recuperiEnabled) return [];
    // Legacy sempre disponibili se recuperi_abilitati; nuovi solo se il rispettivo sospeso è attivo
    const legacy = ["contante", "assegno"];
    const active = new Set([...legacy, ...sospesiMetodi]);
    return ["contante", "assegno", "bonifico", "pos", "postale"]
      .filter((k) => active.has(k))
      .map((k) => SOSPESO_SUBMETHODS[k].recupero);
  };
  const sospesoLabelsAvailable = buildSospesoLabels();
  const recuperoLabelsAvailable = buildRecuperoLabels();
  const METHODS_AGENCY_AVAILABLE = isAgente
    ? [...recuperoLabelsAvailable, ...METHODS_AGENCY, ...sospesoLabelsAvailable]
    : [];
  // Per il collaboratore: se ha sospesi_enabled=true e agenzia ha sospesi_abilitati, aggiungiamo i Sospeso ai metodi.
  const collabHasSospesi = !isAgente && !!user?.sospesi_enabled && sospesiEnabled;
  const METHODS = isAgente
    ? [...METHODS_BASIC, ...METHODS_AGENCY_AVAILABLE]
    : (collabHasSospesi ? [...METHODS_BASIC, ...recuperoLabelsAvailable, ...sospesoLabelsAvailable] : METHODS_BASIC);
  // Per i totali: include tutti i sub-metodi che potrebbero comparire
  const AGENCY_ALL_LABELS = [
    ...Object.values(SOSPESO_SUBMETHODS).map((v) => v.recupero),
    ...Object.values(SOSPESO_SUBMETHODS).map((v) => v.sospeso),
    "Sospeso",
    "Incasso collaboratore",
  ];
  const totalsToShow = isAgente
    ? [...METHODS_BASIC, ...AGENCY_ALL_LABELS.filter((m) => Number(totals[m] || 0) > 0)]
    : METHODS_BASIC;
  const titleDate = useMemo(() => formatDateIT(sheet?.data_foglio_cassa || ""), [sheet]);
  const nomeCollab = useMemo(() => `${user?.nome || ""} ${user?.cognome || ""}`.trim().toUpperCase(), [user]);
  const titleLabel = isAgente ? "FOGLIO CASSA DI AGENZIA" : `FOGLIO CASSA DI ${nomeCollab}`;

  const closeManually = async () => {
    if (!sheet?.id) return;
    if (!window.confirm("Confermi la chiusura del foglio cassa di agenzia? Una volta chiuso non sarà più modificabile.")) return;
    try {
      await api.post(`/cash-sheets/${sheet.id}/close`);
      toast.success("Foglio cassa chiuso");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addEntry = async () => {
    if (!isOpen) return;
    const polizzaRequired = !METHODS_WITHOUT_POLIZZA.includes(draft.metodo_pagamento);
    if (!draft.contraente.trim() && !(showCollabCol && (draft.collab_associato_nome || "").trim())) {
      toast.error("Contraente obbligatorio (oppure seleziona un collaboratore)");
      return;
    }
    if (polizzaRequired && !draft.numero_polizza.trim()) {
      toast.error("Numero polizza obbligatorio per questo metodo");
      return;
    }
    if (plurimandatari && !draft.compagnia_id) {
      toast.error("Seleziona la compagnia per questa polizza");
      return;
    }
    const importo = parseFloat(String(draft.importo).replace(",", "."));
    if (!isFinite(importo)) {
      toast.error("Importo non valido");
      return;
    }
    const abbuonoStr = String(draft.abbuono || "0").replace(",", ".");
    const abbuono = parseFloat(abbuonoStr);
    if (!isFinite(abbuono) || abbuono < 0) {
      toast.error("Abbuono non valido");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/cash-sheets/${sheet.id}/entries`, {
        urgente: !!draft.urgente,
        contraente: draft.contraente.trim(),
        numero_polizza: draft.numero_polizza.trim(),
        importo,
        abbuono,
        metodo_pagamento: draft.metodo_pagamento,
        compagnia_id: plurimandatari ? draft.compagnia_id : null,
        note: draft.note || "",
        collab_associato_nome: showCollabCol ? ((draft.collab_associato_nome || "").trim() || null) : null,
      });
      setDraft(emptyDraft());
      try { localStorage.removeItem(draftStorageKey(sheet.id)); } catch { /* silenzioso */ }
      toast.success(draft.urgente ? "Incasso urgente registrato — notifica inviata" : "Incasso aggiunto");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const updateEntry = async (entry, patch) => {
    if (!isOpen) return;
    try {
      await api.put(`/cash-sheets/${sheet.id}/entries/${entry.id}`, patch);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteEntry = async (entry) => {
    if (!isOpen) return;
    if (!window.confirm("Eliminare questa riga?")) return;
    try {
      await api.delete(`/cash-sheets/${sheet.id}/entries/${entry.id}`);
      toast.success("Riga eliminata");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const downloadFile = async (kind) => {
    try {
      const res = await api.get(`/cash-sheets/${sheet.id}/${kind}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `foglio_cassa_${user?.cognome || ""}_${sheet.data_foglio_cassa}.${kind === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading) return <div className="text-slate-500 text-sm" data-testid="sheet-loading">Caricamento foglio cassa…</div>;
  if (!data || !sheet) return <div className="text-slate-500 text-sm">Foglio cassa non disponibile.</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Foglio cassa</div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900" data-testid="sheet-title">
            {titleLabel} DEL {titleDate}
          </h1>
          <div className="flex items-center gap-2 mt-2">
            <StatusBadge stato={sheet.stato} />
            <span className="inline-flex items-center text-xs text-slate-500 gap-1">
              <Clock className="w-3.5 h-3.5" />
              {isAutonomous
                ? "chiusura manuale o automatica alle 23:59"
                : closingInfo.today_closing
                  ? `chiusura giornaliera prevista alle ${closingInfo.today_closing}`
                  : "oggi nessuna chiusura prevista — gli incassi confluiscono al prossimo giorno lavorativo"}
            </span>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isAutonomous && isOpen && (
            <Button
              variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              onClick={closeManually}
              data-testid="manual-close-btn"
            >
              <Lock className="w-4 h-4 mr-2" /> Chiudi foglio cassa
            </Button>
          )}
          <Button variant="outline" onClick={() => downloadFile("pdf")} data-testid="download-pdf-btn">
            <Download className="w-4 h-4 mr-2" /> PDF
          </Button>
          <Button variant="outline" onClick={() => downloadFile("excel")} data-testid="download-excel-btn">
            <Download className="w-4 h-4 mr-2" /> Excel
          </Button>
        </div>
      </div>

      {!isOpen && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900" data-testid="sheet-closed-banner">
          <strong>Foglio cassa chiuso.</strong> Le righe non sono modificabili. Gli incassi inseriti adesso verranno registrati sul foglio del giorno successivo dopo l&apos;orario di chiusura.
        </div>
      )}

      {(() => {
        const sectionsBelow = !!data?.agency?.sections_below_entries;
        const cashFlowBlock = data.cash_flow && (
          <CashFlowCard
            key="cash-flow"
            flow={data.cash_flow}
            sheetId={sheet.id}
            onUpdated={load}
            canEdit={isOpen}
            isAgency={user?.role === "agente"}
          />
        );
        const expensesBlock = data.can_record_expenses && (
          <ExpensesSection
            key="expenses"
            sheetId={sheet.id}
            expenses={data.expenses || []}
            canEdit={isOpen}
            onChanged={load}
          />
        );
        return !sectionsBelow ? (
          <>
            {cashFlowBlock}
            {expensesBlock}
          </>
        ) : null;
      })()}

      {/* Table */}
      <Card className="overflow-hidden border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left">
                <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 w-16 text-center">Urgente</th>
                <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Contraente</th>
                <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">N. Polizza</th>
                <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right w-32">Importo</th>
                {allowAbbuoni && <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right w-24">Abbuono</th>}
                <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 w-44">Metodo</th>
                {plurimandatari && <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 w-40">Compagnia</th>}
                {showCollabCol && <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 w-40" data-testid="col-header-collab">Collab. associato</th>}
                <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Note</th>
                <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {entries.length === 0 && (
                <tr>
                  <td colSpan={(allowAbbuoni ? 8 : 7) + (plurimandatari ? 1 : 0) + (showCollabCol ? 1 : 0)} className="px-3 py-8 text-center text-slate-500 text-sm" data-testid="empty-state">
                    Nessun incasso registrato. Aggiungi il primo qui sotto.
                  </td>
                </tr>
              )}
              {entries.map((e, idx) => {
                const Icon = METHOD_ICONS[e.metodo_pagamento] || Banknote;
                const locked = isLockedForCollab(e);
                const ro = !isOpen || locked;
                return (
                  <tr key={e.id} className={e.urgente ? "urgent-row" : "hover:bg-slate-50"} data-testid={`entry-row-${idx}`}>
                    <td className="px-3 py-2 text-center">
                      <Checkbox
                        checked={!!e.urgente}
                        disabled={ro}
                        onCheckedChange={(v) => updateEntry(e, { urgente: !!v })}
                        data-testid={`entry-urgent-${idx}`}
                      />
                      {e.urgente && <AlertTriangle className="w-4 h-4 text-red-600 inline-block ml-1" />}
                      {locked && (
                        <span title="Urgente confermato: solo l'agente può modificare" className="inline-block ml-1 align-middle">
                          <Lock className="w-3.5 h-3.5 text-amber-600 inline" />
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        disabled={ro}
                        onBlur={(ev) => ev.target.value !== e.contraente && updateEntry(e, { contraente: ev.target.value })}
                        defaultValue={e.contraente}
                        className="h-8 text-sm"
                        data-testid={`entry-contraente-${idx}`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        disabled={ro}
                        onBlur={(ev) => ev.target.value !== e.numero_polizza && updateEntry(e, { numero_polizza: ev.target.value })}
                        defaultValue={e.numero_polizza}
                        className="h-8 text-sm"
                        data-testid={`entry-polizza-${idx}`}
                      />
                    </td>
                    <td className="px-3 py-2 text-right font-mono-num">
                      <AmountInput
                        disabled={ro}
                        onBlur={(ev) => {
                          const v = parseFloat(ev.target.value);
                          if (isFinite(v) && v !== e.importo) updateEntry(e, { importo: v });
                        }}
                        defaultValue={e.importo}
                        className="h-8 text-sm text-right font-mono-num"
                        data-testid={`entry-importo-${idx}`}
                      />
                    </td>
                    {allowAbbuoni && (
                      <td className="px-3 py-2 text-right font-mono-num">
                        <AmountInput
                          disabled={ro}
                          onBlur={(ev) => {
                            const v = parseFloat(ev.target.value || "0");
                            if (isFinite(v) && v !== (e.abbuono || 0)) updateEntry(e, { abbuono: v });
                          }}
                          defaultValue={e.abbuono || 0}
                          className="h-8 text-sm text-right font-mono-num"
                          data-testid={`entry-abbuono-${idx}`}
                        />
                      </td>
                    )}
                    <td className="px-3 py-2">
                      <Select
                        disabled={ro}
                        value={e.metodo_pagamento}
                        onValueChange={(v) => updateEntry(e, { metodo_pagamento: v })}
                      >
                        <SelectTrigger className="h-8 text-sm" data-testid={`entry-metodo-${idx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {METHODS.map((m) => {
                            const I = METHOD_ICONS[m] || Banknote;
                            return (
                              <SelectItem key={m} value={m}>
                                <span className="inline-flex items-center gap-2"><I className="w-3.5 h-3.5" /> {m}</span>
                              </SelectItem>
                            );
                          })}
                          {/* Legacy: se la entry ha un metodo NON più selezionabile
                              (es. 'Incasso collaboratore' storico), lo mostriamo come
                              opzione dedicata per non far apparire il Select vuoto. */}
                          {e.metodo_pagamento && !METHODS.includes(e.metodo_pagamento) ? (
                            <SelectItem key={e.metodo_pagamento} value={e.metodo_pagamento}>
                              <span className="inline-flex items-center gap-2 text-slate-500 italic">
                                <Banknote className="w-3.5 h-3.5" />
                                {e.metodo_pagamento} (legacy)
                              </span>
                            </SelectItem>
                          ) : null}
                        </SelectContent>
                      </Select>
                    </td>
                    {plurimandatari && (
                      <td className="px-3 py-2">
                        <Select
                          disabled={ro}
                          value={e.compagnia_id || ""}
                          onValueChange={(v) => updateEntry(e, { compagnia_id: v })}
                        >
                          <SelectTrigger className="h-8 text-sm" data-testid={`entry-compagnia-${idx}`}>
                            <SelectValue placeholder="—" />
                          </SelectTrigger>
                          <SelectContent>
                            {activeCompanies.map((c) => (
                              <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                            ))}
                            {!activeCompanies.find((c) => c.id === e.compagnia_id) && e.compagnia_id && (
                              <SelectItem value={e.compagnia_id}>
                                {(companies.find((c) => c.id === e.compagnia_id)?.nome || "Compagnia") + " (disattivata)"}
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </td>
                    )}
                    {showCollabCol && (
                      <td className="px-3 py-2">
                        <CollabAssociatoCell
                          value={e.collab_associato_nome || ""}
                          options={collabOptions}
                          disabled={ro}
                          testId={`entry-collab-${idx}`}
                          onChange={(v) => updateEntry(e, { collab_associato_nome: v })}
                        />
                      </td>
                    )}
                    <td className="px-3 py-2">
                      <Input
                        disabled={ro}
                        onBlur={(ev) => ev.target.value !== (e.note || "") && updateEntry(e, { note: ev.target.value })}
                        defaultValue={e.note || ""}
                        className="h-8 text-sm"
                        data-testid={`entry-note-${idx}`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      {isOpen && !locked && (
                        <button
                          onClick={() => deleteEntry(e)}
                          className="text-slate-400 hover:text-red-600 transition-colors"
                          data-testid={`entry-delete-${idx}`}
                          aria-label="Elimina"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                      {locked && (
                        <span title="Urgente confermato: solo l'agente può eliminare" aria-label="Bloccato">
                          <Lock className="w-4 h-4 text-amber-600" />
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}

              {/* New entry draft row */}
              {isOpen && (
                <tr className={draft.urgente ? "urgent-row" : "bg-slate-50/50"} data-testid="draft-row">
                  <td className="px-3 py-2 text-center">
                    <Checkbox
                      checked={draft.urgente}
                      onCheckedChange={(v) => setDraft({ ...draft, urgente: !!v })}
                      data-testid="draft-urgent"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Input
                      placeholder="Nome contraente"
                      value={draft.contraente}
                      onChange={(e) => setDraft({ ...draft, contraente: e.target.value })}
                      className="h-8 text-sm"
                      data-testid="draft-contraente"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Input
                      placeholder={METHODS_WITHOUT_POLIZZA.includes(draft.metodo_pagamento) ? "N. polizza (facolt.)" : "N. polizza"}
                      value={draft.numero_polizza}
                      onChange={(e) => setDraft({ ...draft, numero_polizza: e.target.value })}
                      className="h-8 text-sm"
                      data-testid="draft-polizza"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <AmountInput
                      placeholder="0,00"
                      value={draft.importo}
                      onChange={(e) => setDraft({ ...draft, importo: e.target.value })}
                      className="h-8 text-sm text-right font-mono-num"
                      data-testid="draft-importo"
                    />
                  </td>
                  {allowAbbuoni && (
                    <td className="px-3 py-2">
                      <AmountInput
                        placeholder="0,00"
                        value={draft.abbuono}
                        onChange={(e) => setDraft({ ...draft, abbuono: e.target.value })}
                        className="h-8 text-sm text-right font-mono-num"
                        data-testid="draft-abbuono"
                      />
                    </td>
                  )}
                  <td className="px-3 py-2">
                    <Select value={draft.metodo_pagamento} onValueChange={(v) => setDraft({ ...draft, metodo_pagamento: v })}>
                      <SelectTrigger className="h-8 text-sm" data-testid="draft-metodo">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  {plurimandatari && (
                    <td className="px-3 py-2">
                      <Select value={draft.compagnia_id} onValueChange={(v) => setDraft({ ...draft, compagnia_id: v })}>
                        <SelectTrigger className="h-8 text-sm" data-testid="draft-compagnia">
                          <SelectValue placeholder={activeCompanies.length ? "Seleziona…" : "Aggiungi compagnie in Impostazioni"} />
                        </SelectTrigger>
                        <SelectContent>
                          {activeCompanies.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                  )}
                  {showCollabCol && (
                    <td className="px-3 py-2">
                      <CollabAssociatoCell
                        value={draft.collab_associato_nome || ""}
                        options={collabOptions}
                        testId="draft-collab"
                        dropUp
                        onChange={(v) => setDraft({ ...draft, collab_associato_nome: v })}
                      />
                    </td>
                  )}
                  <td className="px-3 py-2">
                    <Input
                      placeholder="Note (facoltative)"
                      value={draft.note}
                      onChange={(e) => setDraft({ ...draft, note: e.target.value })}
                      className="h-8 text-sm"
                      data-testid="draft-note"
                    />
                  </td>
                  <td className="px-3 py-2"></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {isOpen && (
          <div className="border-t border-slate-200 bg-white p-3 flex flex-wrap gap-2 justify-end">
            {multiEnabled && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setMultiOpen(true)}
                disabled={saving}
                data-testid="open-multi-polizza-btn"
              >
                <Layers className="w-4 h-4 mr-2" /> Incasso multiplo
              </Button>
            )}
            <Button onClick={addEntry} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-entry-btn">
              <Plus className="w-4 h-4 mr-2" /> {saving ? "Salvataggio…" : "Aggiungi incasso"}
            </Button>
          </div>
        )}
      </Card>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="totals-grid">
        {totalsToShow.map((m) => {
          const Icon = METHOD_ICONS[m] || Banknote;
          return (
            <Card key={m} className="p-4 border-slate-200">
              <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider font-semibold">
                <Icon className="w-3.5 h-3.5" /> {m}
              </div>
              <div className="text-xl font-heading font-bold text-slate-900 mt-1 font-mono-num" data-testid={`total-${m.toLowerCase().replace(/ /g, "-")}`}>
                {formatEUR(totals[m])}
              </div>
            </Card>
          );
        })}
        <Card className="p-4 bg-amber-50 border-amber-300 col-span-2 md:col-span-2 lg:col-span-1">
          <div className="text-xs uppercase tracking-wider font-semibold text-amber-800">Totale Liquidità</div>
          <div className="text-[10px] text-amber-700 mt-0.5">Contante + Assegno + Recuperi</div>
          <div className="text-xl font-heading font-bold text-amber-900 mt-1 font-mono-num" data-testid="total-liquidita">
            {formatEUR(totals.totale_liquidita || 0)}
          </div>
        </Card>
        <Card className="p-4 bg-slate-900 text-white col-span-2 md:col-span-4 lg:col-span-1">
          <div className="text-xs uppercase tracking-wider font-semibold text-slate-300">Totale Generale</div>
          <div className="text-2xl font-heading font-bold mt-1 font-mono-num" data-testid="total-generale">
            {formatEUR(totals.totale_generale)}
          </div>
        </Card>
      </div>

      <CompanyTotalsCard sheet={sheet} groups={data?.per_compagnia} cognome={user?.cognome} />

      {/* Sezioni mostrate SOTTO la lista incassi quando l'agente ha attivato l'opzione */}
      {data?.agency?.sections_below_entries && (
        <>
          {data.cash_flow && (
            <CashFlowCard
              flow={data.cash_flow}
              sheetId={sheet.id}
              onUpdated={load}
              canEdit={isOpen}
              isAgency={user?.role === "agente"}
            />
          )}
          {data.can_record_expenses && (
            <ExpensesSection
              sheetId={sheet.id}
              expenses={data.expenses || []}
              canEdit={isOpen}
              onChanged={load}
            />
          )}
        </>
      )}
      <MultiPolizzaDialog
        open={multiOpen}
        onOpenChange={setMultiOpen}
        sheetId={sheet?.id}
        methods={METHODS}
        allowAbbuoni={allowAbbuoni}
        plurimandatari={plurimandatari}
        companies={companies}
        defaultMethod={draft.metodo_pagamento || "Contante"}
        onSuccess={load}
      />
    </div>
  );
}

function StatusBadge({ stato }) {
  const map = {
    aperto: { label: "Aperto", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    chiuso_auto: { label: "Chiuso automaticamente", cls: "bg-slate-100 text-slate-700 border-slate-200" },
    chiuso_manuale: { label: "Chiuso manualmente", cls: "bg-slate-100 text-slate-700 border-slate-200" },
    riaperto: { label: "Riaperto", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  };
  const it = map[stato] || { label: stato, cls: "bg-slate-100 text-slate-700" };
  return (
    <Badge variant="outline" className={`${it.cls} rounded-full px-3 py-0.5 text-xs font-semibold`} data-testid="sheet-status">
      {it.label}
    </Badge>
  );
}

/**
 * Combobox-like input per la colonna "Collab. associato":
 * - Digiti liberamente il nome (necessario per modalità "custom" / "mista").
 * - Un elenco a tendina di suggerimenti (`options`) appare al focus/typing.
 * - onChange è chiamato onBlur e alla selezione di un suggerimento.
 * - "" (vuoto) = nessun collaboratore associato.
 */
function CollabAssociatoCell({ value, options, disabled, testId, onChange, dropUp = false }) {
  const [local, setLocal] = useState(value || "");
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const [rect, setRect] = useState(null);
  useEffect(() => { setLocal(value || ""); }, [value]);
  // Ricalcola la posizione del popup ogni volta che si apre (o si scrolla)
  useEffect(() => {
    if (!open || !inputRef.current) return;
    const update = () => {
      if (!inputRef.current) return;
      const r = inputRef.current.getBoundingClientRect();
      setRect({ left: r.left, top: r.bottom + 2, bottom: r.top - 2, width: r.width });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);
  const filtered = (options || []).filter((o) => {
    const q = (local || "").trim().toLowerCase();
    if (!q) return true;
    return o.toLowerCase().includes(q);
  });
  const commit = (v) => {
    const clean = (v ?? "").trim().slice(0, 60);
    if (clean !== (value || "")) onChange(clean);
    setLocal(clean);
  };
  const popupStyle = rect
    ? (dropUp
        ? { position: "fixed", left: rect.left, bottom: window.innerHeight - rect.bottom, width: Math.max(180, rect.width), zIndex: 9999 }
        : { position: "fixed", left: rect.left, top: rect.top, width: Math.max(180, rect.width), zIndex: 9999 })
    : null;
  return (
    <div className="relative">
      <Input
        ref={inputRef}
        list=""
        disabled={disabled}
        value={local}
        placeholder="—"
        onFocus={() => setOpen(true)}
        onChange={(e) => { setLocal(e.target.value); setOpen(true); }}
        onBlur={() => { setTimeout(() => setOpen(false), 200); commit(local); }}
        className="h-8 text-sm"
        data-testid={testId}
      />
      {open && filtered.length > 0 && !disabled && popupStyle
        ? createPortal(
            <div
              style={popupStyle}
              className="bg-white border border-slate-200 rounded-md shadow-xl max-h-56 overflow-y-auto"
              data-testid={`${testId}-suggestions`}
            >
              {local ? (
                <button
                  type="button"
                  className="w-full text-left px-2 py-1.5 text-xs text-red-600 hover:bg-slate-50"
                  onMouseDown={(e) => { e.preventDefault(); commit(""); setOpen(false); }}
                >
                  Nessuno (rimuovi)
                </button>
              ) : null}
              {filtered.map((o) => (
                <button
                  key={o}
                  type="button"
                  className="w-full text-left px-2 py-1.5 text-sm hover:bg-slate-100"
                  onMouseDown={(e) => { e.preventDefault(); commit(o); setOpen(false); }}
                >
                  {o}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
